import React, { useMemo } from 'react';
import { Box, Chip, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { alpha } from '@mui/material/styles';
import type { MappingTableRowData } from './types';

export interface MappingGroupHeaderProps {
  targetTable: string;
  rows: MappingTableRowData[];
  colSpan?: number;
  onPreview?: (targetTable: string) => void;
}

const MappingGroupHeader: React.FC<MappingGroupHeaderProps> = ({ targetTable, rows, colSpan = 8, onPreview }) => {
  const { mappedCount, approvedCount, totalCount, avgConfidence, sourceTables } = useMemo(() => {
    let mapped = 0;
    let approved = 0;
    let confidenceSum = 0;
    const sources = new Set<string>();
    for (const r of rows) {
      if (r.isMapped) {
        mapped++;
        confidenceSum += r.confidence;
        if (r.sourceTable) sources.add(r.sourceTable);
      }
      if (r.approvalStatus === 'approved') approved++;
    }
    return {
      mappedCount: mapped,
      approvedCount: approved,
      totalCount: rows.length,
      avgConfidence: mapped > 0 ? confidenceSum / mapped : 0,
      sourceTables: Array.from(sources),
    };
  }, [rows]);

  const tone = '#0891b2'; // teal for target tables

  return (
    <TableRow
      sx={{
        backgroundColor: alpha(tone, 0.06),
        '&:hover': { backgroundColor: alpha(tone, 0.1) },
      }}
    >
      <TableCell colSpan={colSpan} sx={{ py: 1, px: 2, borderBottom: `2px solid ${alpha(tone, 0.15)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 1,
              backgroundColor: tone,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: tone,
              fontSize: 14,
              letterSpacing: '-0.01em',
            }}
          >
            {targetTable}
          </Typography>
          {sourceTables.length > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
              ← {sourceTables.join(', ')}
            </Typography>
          )}
          <Chip
            size="small"
            label={`${mappedCount}/${totalCount} mapped`}
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: alpha(tone, 0.1),
              color: tone,
              border: `1px solid ${alpha(tone, 0.2)}`,
            }}
          />
          {approvedCount > 0 && (
            <Chip
              size="small"
              label={`${approvedCount} approved`}
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: alpha('#10b981', 0.1),
                color: '#10b981',
                border: `1px solid ${alpha('#10b981', 0.2)}`,
              }}
            />
          )}
          {avgConfidence > 0 && (
            <Chip
              size="small"
              label={`${Math.round(avgConfidence * 100)}% avg`}
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: alpha('#6366f1', 0.1),
                color: '#6366f1',
                border: `1px solid ${alpha('#6366f1', 0.2)}`,
              }}
            />
          )}
          {onPreview && (
            <Tooltip title="Preview mapped data" arrow>
              <IconButton
                size="small"
                onClick={() => onPreview(targetTable)}
                sx={{ p: 0.25, ml: 'auto', color: '#94a3b8', '&:hover': { color: tone } }}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default React.memo(MappingGroupHeader);
